import React, { useEffect, useState } from 'react';
import { productosApi, catalogosApi, exportApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Pencil, Trash2, Plus, Package, Upload, Download } from 'lucide-react';
import { toast } from 'sonner';
import ImportadorCSV from '../components/ImportadorCSV';
import AiButton from '../components/AiButton';

export default function Productos() {
  const { isAdmin, user } = useAuth();
  const canWrite = isAdmin || user?.rol === 'dir_comercial';
  const [items, setItems] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [q, setQ] = useState('');
  const [familiaFilter, setFamiliaFilter] = useState('__all__');
  const [dialog, setDialog] = useState({ open: false, data: null });
  const [showImport, setShowImport] = useState(false);

  const blank = {
    referencia: '', descripcion: '', familia: '', subfamilia: '',
    precio_tarifa: '', unidad_medida: '', normas: [], activo: true, notas: '',
  };

  const load = async () => {
    const [its, fams] = await Promise.all([
      productosApi.list({ q: q || undefined, familia: familiaFilter !== '__all__' ? familiaFilter : undefined }),
      catalogosApi.list('familias_producto').catch(() => []),
    ]);
    setItems(its);
    setFamilias(fams);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, familiaFilter]);

  const save = async () => {
    try {
      const d = { ...dialog.data };
      if (d.precio_tarifa === '' || d.precio_tarifa === null) delete d.precio_tarifa;
      else d.precio_tarifa = Number(d.precio_tarifa);
      if (!d.familia) delete d.familia;
      if (d.id) {
        const id = d.id; delete d.id;
        await productosApi.update(id, d);
        toast.success('Producto actualizado');
      } else {
        await productosApi.create(d);
        toast.success('Producto creado');
      }
      setDialog({ open: false, data: null });
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Desactivar este producto? (soft-delete)')) return;
    await productosApi.delete(id);
    toast.success('Producto desactivado');
    load();
  };

  return (
    <div className="space-y-6" data-testid="productos-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-ink-900 font-display tracking-tight">Productos</h1>
          <p className="text-sm text-ink-500 mt-1">Catálogo interno de referencias propias del fabricante</p>
        </div>
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={async () => { try { await exportApi.descargar('productos'); toast.success('CSV descargado'); } catch (e) { toast.error(e.message); } }} data-testid="btn-export-productos">
              <Download className="w-4 h-4 mr-2" /> Exportar
            </Button>
            <Button variant="outline" onClick={() => setShowImport(true)} data-testid="btn-import-productos">
              <Upload className="w-4 h-4 mr-2" /> Importar CSV
            </Button>
            <Button
              onClick={() => setDialog({ open: true, data: blank })}
              data-testid="btn-nuevo-producto"
            >
              <Plus className="w-4 h-4 mr-2" /> Nuevo producto
            </Button>
          </div>
        )}
      </div>

      <div className="flex gap-3 items-center">
        <Input
          placeholder="Buscar por referencia o descripción..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-md"
          data-testid="input-buscar-productos"
        />
        <Select value={familiaFilter} onValueChange={setFamiliaFilter}>
          <SelectTrigger className="w-64"><SelectValue placeholder="Familia" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas las familias</SelectItem>
            {familias.map((f) => <SelectItem key={f.valor} value={f.valor}>{f.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-lg border border-ink-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 text-ink-600 uppercase text-xs">
            <tr>
              <th className="text-left px-4 py-3">Referencia</th>
              <th className="text-left px-4 py-3">Descripción</th>
              <th className="text-left px-4 py-3">Familia</th>
              <th className="text-right px-4 py-3">Precio tarifa</th>
              <th className="text-left px-4 py-3">Ud.</th>
              <th className="text-center px-4 py-3">Estado</th>
              <th className="text-right px-4 py-3 w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-ink-400">Sin productos registrados</td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="hover:bg-ink-50" data-testid={`producto-row-${p.referencia}`}>
                <td className="px-4 py-3 font-mono font-semibold text-ink-900">{p.referencia}</td>
                <td className="px-4 py-3">{p.descripcion}</td>
                <td className="px-4 py-3 text-ink-500">{p.familia_label || '—'}</td>
                <td className="px-4 py-3 text-right tabular-nums">{p.precio_tarifa != null ? `${p.precio_tarifa.toFixed(2)} €` : '—'}</td>
                <td className="px-4 py-3 text-ink-500">{p.unidad_medida || '—'}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${p.activo ? 'bg-green-100 text-green-700' : 'bg-ink-100 text-ink-500'}`}>
                    {p.activo ? 'activo' : 'inactivo'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {canWrite && (
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => setDialog({ open: true, data: { ...p, normas: p.normas || [] } })}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(p.id)} className="text-rose-600 hover:text-rose-700">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Dialog crear/editar */}
      <Dialog open={dialog.open} onOpenChange={(v) => setDialog({ open: v, data: v ? dialog.data : null })}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Package className="w-4 h-4" />{dialog.data?.id ? 'Editar producto' : 'Nuevo producto'}</DialogTitle>
          </DialogHeader>
          {dialog.data && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Referencia *</Label>
                  <Input value={dialog.data.referencia} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, referencia: e.target.value } }))} data-testid="input-referencia" />
                </div>
                <div>
                  <Label>Unidad medida</Label>
                  <Input value={dialog.data.unidad_medida || ''} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, unidad_medida: e.target.value } }))} placeholder="ml, ud, kg..." />
                </div>
              </div>
              <div>
                <Label>Descripción *</Label>
                <Input value={dialog.data.descripcion} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, descripcion: e.target.value } }))} data-testid="input-descripcion" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Familia</Label>
                  <Select value={dialog.data.familia || '__none__'} onValueChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, familia: v === '__none__' ? '' : v } }))}>
                    <SelectTrigger><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {familias.map((f) => <SelectItem key={f.valor} value={f.valor}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Precio tarifa (€)</Label>
                  <Input type="number" step="0.01" value={dialog.data.precio_tarifa ?? ''} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, precio_tarifa: e.target.value } }))} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Notas</Label>
                  <AiButton
                    feature="corregir"
                    value={dialog.data.notas || ''}
                    onAccept={(nuevo) => setDialog((d) => ({ ...d, data: { ...d.data, notas: nuevo } }))}
                  />
                </div>
                <Textarea rows={3} value={dialog.data.notas || ''} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, notas: e.target.value } }))} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!dialog.data.activo} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, activo: e.target.checked } }))} />
                Activo (visible en selecciones)
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, data: null })}>Cancelar</Button>
            <Button onClick={save} data-testid="btn-guardar-producto">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Importador CSV */}
      <ImportadorCSV
        open={showImport}
        onOpenChange={(v) => { if (!v) load(); setShowImport(v); }}
        tipo="productos"
        titulo="Importar productos"
        onCompleted={load}
      />
    </div>
  );
}
